import { FC, MouseEvent, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { merge } from "lodash";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { generateRoute, makeSxStyles } from "ui";
import { formattedValueWithSymbol } from "@/shared/utils/string-helpers";
import { SkippedOrderRegionGroup } from "../hooks/useSkippedOrders";
import useSkippedOrdersSelection from "../hooks/useSkippedOrdersSelection";
import useRestoreSkippedOrders from "../hooks/useRestoreSkippedOrders";
import { SnowLevelIcon } from "./icons";
import { AdRoutes } from "@/constants/ad-routes";
import useCreateTempRouteStore from "@/state/create-temp-route-store";
import useRoutePlannerStore from "@/state/route-planner-store";
import { MapMarker } from "@/components/map/map.types";

export interface SkippedOrdersModalProps {
  date: string;
  regionGroups: SkippedOrderRegionGroup[];
  onClose: () => void;
  onRestored: () => void;
}

const formatLastPlowed = (value: string | null | undefined): string =>
  value ? DateTime.fromISO(value).toFormat("d MMMM yyyy") : "N/A";

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const SkippedOrdersModal: FC<SkippedOrdersModalProps> = ({
  date,
  regionGroups,
  onClose,
  onRestored,
}) => {
  const styles = useStyles();
  const [expandedRegionIds, setExpandedRegionIds] = useState<Set<string>>(
    new Set(),
  );
  const {
    selectedIds,
    selectedCount,
    isAllSelected,
    toggleAll,
    isRegionSelected,
    isRegionPartiallySelected,
    getRegionSelectedCount,
    toggleRegion,
    isOrderSelected,
    toggleOrder,
    clear,
  } = useSkippedOrdersSelection(regionGroups);
  const { restore, isRestoring } = useRestoreSkippedOrders(date);
  const navigate = useNavigate();
  const initCreateTempRoute = useCreateTempRouteStore((state) => state.init);
  // Live date vs. the frozen `date` this modal opened with (see
  // SkippedOrdersButton's modalDate) - catches drift from e.g. End Shift.
  const liveRoutePlannerDate = useRoutePlannerStore((state) => state.date);

  const totalCount = regionGroups.reduce(
    (sum, group) => sum + group.locations.length,
    0,
  );

  const toggleExpanded = (regionId: string) => {
    setExpandedRegionIds((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return next;
    });
  };

  const expandAll = () =>
    setExpandedRegionIds(new Set(regionGroups.map((group) => group.regionId)));
  const collapseAll = () => setExpandedRegionIds(new Set());

  const handleRegionRowClick = (regionId: string) => {
    const willSelect = !isRegionSelected(regionId);
    toggleRegion(regionId);
    if (willSelect) {
      setExpandedRegionIds((prev) => new Set(prev).add(regionId));
    }
  };

  const isExpandAllDisabled =
    regionGroups.length === 0 || expandedRegionIds.size === regionGroups.length;
  const isCollapseAllDisabled = expandedRegionIds.size === 0;

  const handleRestore = async () => {
    try {
      await restore(Array.from(selectedIds));
      toast.success("Selected orders were restored to their routes.");
      clear();
      onClose();
    } catch {
      // Toast error is handled globally.
    } finally {
      onRestored();
    }
  };

  const handleCreateTemporaryRoute = () => {
    if (liveRoutePlannerDate && liveRoutePlannerDate !== date) {
      toast.error(
        "The Route Planner has moved to a different day since this list " +
          "was opened. Close this and reopen Skipped Orders to continue.",
      );
      return;
    }

    const selectedLocations = regionGroups
      .flatMap((region) =>
        region.locations.map((location) => ({ location, region })),
      )
      .filter(({ location }) => selectedIds.has(location.locationId));

    const markers: MapMarker[] = selectedLocations.map(
      ({ location, region }) => ({
        id: location.locationId,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        locationSequence: null,
        region: {
          id: region.regionId,
          name: region.regionName,
          color: region.regionColor,
        },
      }),
    );

    initCreateTempRoute(date, markers);
    onClose();
    navigate(
      generateRoute(AdRoutes.createTempRouteSequence, {
        date: DateTime.fromISO(date).toFormat("MM-dd-yyyy"),
      }),
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: styles.paper }}
    >
      <Box sx={styles.content}>
        <Typography sx={styles.title}>Skipped Orders</Typography>
        <Box sx={styles.innerContainer}>
          <Typography sx={styles.subtitle}>
            Showing {totalCount} orders with status &quot;Skip&quot; for date{" "}
            {DateTime.fromISO(date).toFormat("MM/dd/yyyy")}.
            <br />
            Review the orders below and select which ones to return to their
            original routes or add to a temporary route.
          </Typography>
          <Box sx={styles.expandActions}>
            <Typography
              sx={merge(
                {},
                styles.linkButton,
                isExpandAllDisabled && styles.linkButtonDisabled,
              )}
              onClick={isExpandAllDisabled ? undefined : expandAll}
            >
              Expand All
            </Typography>
            <Typography
              sx={merge(
                {},
                styles.linkButton,
                isCollapseAllDisabled && styles.linkButtonDisabled,
              )}
              onClick={isCollapseAllDisabled ? undefined : collapseAll}
            >
              Collapse All
            </Typography>
          </Box>

          <Box sx={styles.tableWrapper}>
            <Box sx={styles.tableHeaderRow}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={selectedCount > 0 && !isAllSelected}
                onChange={toggleAll}
                sx={styles.checkbox}
                data-testid="select-all-skipped-orders"
              />
              <Box sx={styles.headerCellAddress}>
                <Typography sx={styles.headerCellText}>
                  Route / Address
                </Typography>
              </Box>
              <Box sx={styles.headerCell}>
                <Typography sx={styles.headerCellText}>Last Plowed</Typography>
              </Box>
              <Box sx={styles.headerCell}>
                <Typography sx={styles.headerCellText}>Snow</Typography>
              </Box>
            </Box>

            <Box sx={styles.scrollArea}>
              {regionGroups.map((region) => {
                const isExpanded = expandedRegionIds.has(region.regionId);
                const regionSelectedCount = getRegionSelectedCount(
                  region.regionId,
                );
                return (
                  <Box key={region.regionId}>
                    <Box
                      sx={merge(
                        {},
                        styles.regionRow,
                        styles.clickableRow,
                        !isExpanded && styles.rowDivider,
                      )}
                      onClick={() => handleRegionRowClick(region.regionId)}
                    >
                      <Box sx={styles.regionRowLeft}>
                        <Checkbox
                          checked={isRegionSelected(region.regionId)}
                          indeterminate={isRegionPartiallySelected(
                            region.regionId,
                          )}
                          onChange={() => toggleRegion(region.regionId)}
                          onClick={stopPropagation}
                          sx={styles.checkbox}
                          data-testid={`select-region-${region.regionId}`}
                        />
                        <Box sx={styles.regionNameGroup}>
                          <Box
                            sx={{
                              ...styles.regionColorBar,
                              background: region.regionColor ?? "#8B9198",
                            }}
                          />
                          <Typography sx={styles.regionName}>
                            {region.regionName} ({region.locations.length})
                          </Typography>
                        </Box>
                        {regionSelectedCount > 0 && (
                          <Box sx={styles.chip}>
                            <Typography sx={styles.chipText}>
                              {regionSelectedCount} selected
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <IconButton
                        onClick={(event) => {
                          stopPropagation(event);
                          toggleExpanded(region.regionId);
                        }}
                        data-testid={`toggle-region-${region.regionId}`}
                      >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                    <Collapse in={isExpanded}>
                      {region.locations.map((location, index) => (
                        <Box
                          key={location.locationId}
                          sx={merge(
                            {},
                            styles.orderRow,
                            styles.clickableRow,
                            index === region.locations.length - 1 &&
                              styles.rowDivider,
                          )}
                          onClick={() =>
                            toggleOrder(region.regionId, location.locationId)
                          }
                        >
                          <Checkbox
                            checked={isOrderSelected(location.locationId)}
                            onChange={() =>
                              toggleOrder(region.regionId, location.locationId)
                            }
                            onClick={stopPropagation}
                            sx={styles.checkbox}
                            data-testid={`select-order-${location.locationId}`}
                          />
                          <Box sx={styles.orderAddressCell}>
                            <Typography sx={styles.orderAddress}>
                              {location.address}
                            </Typography>
                          </Box>
                          <Box sx={styles.orderCell}>
                            <Typography sx={styles.orderCellText}>
                              {formatLastPlowed(location.lastPlowedAt)}
                            </Typography>
                          </Box>
                          <Box sx={styles.orderCell}>
                            <Box sx={styles.snowValue}>
                              <SnowLevelIcon />
                              <Typography sx={styles.orderCellText}>
                                {formattedValueWithSymbol(
                                  location.snowAmount.imperial,
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.footer}>
        <Typography sx={styles.selectionCount}>
          {selectedCount} of {totalCount} selected
        </Typography>
        <Box sx={styles.footerActions}>
          <Button
            onClick={onClose}
            sx={styles.cancelButton}
            data-testid="cancel-skipped-orders"
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={selectedCount === 0}
            onClick={handleCreateTemporaryRoute}
            data-testid="create-temporary-route"
          >
            Create Temporary Route
          </Button>
          <Button
            variant="contained"
            disabled={selectedCount === 0 || isRestoring}
            onClick={handleRestore}
            data-testid="restore-to-routes"
          >
            Restore to Routes
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  paper: {
    padding: 0,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px 24px 8px",
  },
  innerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    width: "100%",
  },
  title: {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: grey[100],
  },
  subtitle: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: grey[200],
  },
  expandActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "24px",
  },
  tableWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  linkButton: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.1px",
    color: "#8ACEFF",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  linkButtonDisabled: {
    color: grey[400],
    cursor: "default",
    "&:hover": { textDecoration: "none" },
  },
  checkbox: {
    padding: 0,
  },
  tableHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "40px",
    padding: "0 8px",
    borderBottom: `1px solid ${grey[400]}`,
  },
  headerCellAddress: {
    flex: "1 0 0",
    minWidth: 0,
    padding: "8px 16px",
  },
  headerCell: {
    width: "120px",
    padding: "8px 16px",
  },
  headerCellText: {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    color: grey[100],
    whiteSpace: "nowrap",
  },
  scrollArea: {
    maxHeight: "400px",
    overflowY: "auto",
  },
  regionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "48px",
    padding: "5px 8px",
  },
  rowDivider: {
    borderBottom: `1px solid ${grey[400]}`,
  },
  clickableRow: {
    cursor: "pointer",
  },
  regionRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  regionNameGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  regionColorBar: {
    width: "6px",
    height: "13px",
    borderRadius: "2px",
  },
  regionName: {
    fontFamily: "Roboto",
    fontSize: "14px",
    lineHeight: "1.43",
    letterSpacing: "0.15px",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
  },
  chip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    background: "rgba(197, 198, 201, 0.15)",
    padding: "4px 8px",
  },
  chipText: {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "16px",
    color: grey[100],
    whiteSpace: "nowrap",
  },
  orderRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "40px",
    padding: "0 8px",
    background: grey[700],
  },
  orderAddressCell: {
    flex: "1 0 0",
    minWidth: 0,
    padding: "0 16px",
  },
  orderAddress: {
    fontFamily: "Inter",
    fontSize: "14px",
    letterSpacing: "0.1px",
    color: grey[100],
    whiteSpace: "nowrap",
  },
  orderCell: {
    width: "120px",
    padding: "0 16px",
  },
  snowValue: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  orderCellText: {
    fontFamily: "Inter",
    fontSize: "14px",
    letterSpacing: "0.1px",
    color: grey[100],
    whiteSpace: "nowrap",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "24px",
    borderTop: `1px solid ${grey[400]}`,
  },
  selectionCount: {
    fontFamily: "Inter",
    fontSize: "14px",
    color: grey[200],
  },
  footerActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cancelButton: {
    color: "#8ACEFF",
    textTransform: "none",
    borderRadius: "100px",
    padding: "10px 12px",
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
  },
}));

export default SkippedOrdersModal;
